import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  groceryApi,
  type GroceryItem,
  type GrocerySelectionInput,
  type Recipe,
  recipesApi,
} from "@/client/api";
import { GroceryItemRow } from "@/client/components/GroceryItemRow";
import { TypeSection } from "@/client/components/TypeSection";
import { Card, EmptyState, Input, PageHeader, Select, Spinner } from "@/client/components/ui";
import { RecipeSelect } from "@/client/components/ui/RecipeSelect";
import { buildClipboardText } from "@/client/computations/grocery";
import {
  INGREDIENT_TYPE_EMOJIS,
  INGREDIENT_TYPE_LABELS,
  INGREDIENT_TYPE_ORDER,
} from "@/client/constants";
import { useToast } from "@/client/context/ToastContext";
import { useApi } from "@/client/hooks/useApi";
import { useCyclingIndex } from "@/client/hooks/useCyclingIndex";
import { CopyIcon, DeleteIcon, GroceryIcon } from "@/client/icons";

type Phase = "meal-prep" | "day-of-eating" | "both";

interface Row {
  key: string;
  recipeId: string;
  phase: Phase;
  servings: string;
  variantId: string | null;
}

const PHASE_OPTIONS = [
  { value: "meal-prep", label: "Meal prep" },
  { value: "day-of-eating", label: "Día de" },
  { value: "both", label: "Ambos" },
];

let rowSeq = 0;
const newRow = (): Row => ({
  key: `row_${rowSeq++}`,
  recipeId: "",
  phase: "meal-prep",
  servings: "",
  variantId: null,
});

/** One recipe selection: recipe + (variant) + phase + servings. */
function SelectionRow({
  row,
  recipes,
  onChange,
  onRemove,
}: {
  row: Row;
  recipes: Recipe[];
  onChange: (updated: Row) => void;
  onRemove: () => void;
}) {
  const [variants, setVariants] = useState<{ id: string; name: string }[]>([]);

  // Load the recipe's variants when it changes.
  /* eslint-disable react-hooks/set-state-in-effect -- syncing variant list from async fetch */
  useEffect(() => {
    if (!row.recipeId) {
      setVariants([]);
      return;
    }
    let stale = false;
    recipesApi
      .get(row.recipeId)
      .then((detail) => {
        if (!stale) setVariants(detail.variants ?? []);
      })
      .catch(() => {});
    return () => {
      stale = true;
    };
  }, [row.recipeId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex-1 min-w-[200px]">
        <RecipeSelect
          recipes={recipes}
          value={row.recipeId}
          onChange={(id) => {
            const picked = recipes.find((r) => r.id === id);
            onChange({
              ...row,
              recipeId: id,
              variantId: null,
              // Default servings to the recipe's yield (porciones).
              servings: picked?.servings != null ? String(picked.servings) : row.servings,
            });
          }}
          placeholder="Buscar receta..."
        />
      </div>
      {variants.length > 0 && (
        <div className="w-40">
          <Select
            value={row.variantId ?? ""}
            onChange={(v) => onChange({ ...row, variantId: v || null })}
            options={variants.map((v) => ({ value: v.id, label: v.name }))}
            placeholder="Variante..."
          />
        </div>
      )}
      <div className="w-36">
        <Select
          value={row.phase}
          onChange={(v) => onChange({ ...row, phase: v as Phase })}
          options={PHASE_OPTIONS}
        />
      </div>
      <div className="w-24">
        <Input
          value={row.servings}
          onChange={(v) => onChange({ ...row, servings: v })}
          type="number"
          placeholder="porc."
        />
      </div>
      <button
        onClick={onRemove}
        aria-label="Quitar receta"
        className="text-term-text-muted hover:text-term-error cursor-pointer shrink-0 p-1.5 rounded-lg hover:bg-term-error/10 transition-colors"
      >
        <DeleteIcon width={16} height={16} />
      </button>
    </div>
  );
}

export function ShoppingListPage() {
  const { toast } = useToast();
  const recipesFetcher = useCallback(() => recipesApi.list(), []);
  const { data: recipes } = useApi(recipesFetcher, []);

  const [rows, setRows] = useState<Row[]>([newRow()]);
  const [repIndex, cycleRep] = useCyclingIndex();
  const [checked, setChecked] = useState<Set<string>>(new Set());

  function updateRow(updated: Row) {
    setRows((prev) => prev.map((r) => (r.key === updated.key ? updated : r)));
  }
  function removeRow(key: string) {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : [newRow()]));
  }
  function toggleChecked(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Build the API payload from valid rows (recipe selected + positive servings).
  const selections: GrocerySelectionInput[] = useMemo(
    () =>
      rows
        .filter((r) => r.recipeId && parseFloat(r.servings) > 0)
        .map((r) => ({
          recipe_id: r.recipeId,
          phase: r.phase,
          servings: parseFloat(r.servings),
          variant_id: r.variantId,
        })),
    [rows],
  );
  const serialized = JSON.stringify(selections);

  const { data, loading } = useApi(
    () =>
      selections.length > 0
        ? groceryApi.fromSelections(selections)
        : Promise.resolve({ items: {} as Record<string, GroceryItem[]> }),
    [serialized],
  );

  const items = data?.items ?? {};
  const hasItems = Object.keys(items).length > 0;

  function copyAll() {
    const text = buildClipboardText(
      items,
      repIndex,
      INGREDIENT_TYPE_ORDER,
      INGREDIENT_TYPE_EMOJIS,
      INGREDIENT_TYPE_LABELS,
    );
    navigator.clipboard.writeText(text);
    toast("Lista copiada");
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto flex flex-col gap-5 md:gap-6">
      <PageHeader
        icon={<GroceryIcon width={24} height={24} />}
        title="Lista de compras"
        subtitle="Agregá recetas y obtené la lista de ingredientes — sin plan ni sesión."
      />

      <Card className="p-4 flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          {rows.map((row) => (
            <SelectionRow
              key={row.key}
              row={row}
              recipes={recipes ?? []}
              onChange={updateRow}
              onRemove={() => removeRow(row.key)}
            />
          ))}
        </div>
        <button
          onClick={() => setRows((prev) => [...prev, newRow()])}
          className="text-sm text-term-accent hover:bg-term-accent/10 cursor-pointer text-left px-2 py-1 rounded-md transition-colors w-fit"
        >
          + agregar receta
        </button>
      </Card>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-lg text-term-text">Ingredientes</h2>
          {hasItems && (
            <button
              onClick={copyAll}
              className="text-sm text-term-text-muted hover:text-term-accent cursor-pointer transition-colors flex items-center gap-1"
            >
              <CopyIcon width={14} height={14} className="shrink-0" aria-hidden />
              Copiar todo
            </button>
          )}
        </div>

        {loading && selections.length > 0 && <Spinner className="!py-6" />}
        {!hasItems && !loading && (
          <EmptyState
            icon={<GroceryIcon width={28} height={28} />}
            title="Sin ingredientes todavía"
            description="Agregá al menos una receta con porciones para ver la lista."
          />
        )}
        {hasItems && (
          <Card className="p-4">
            {INGREDIENT_TYPE_ORDER.filter((type) => items[type]?.length).map((type) => (
              <TypeSection
                key={type}
                emoji={INGREDIENT_TYPE_EMOJIS[type] ?? "📦"}
                label={INGREDIENT_TYPE_LABELS[type] ?? type}
              >
                {items[type]!.map((item, i) => (
                  <GroceryItemRow
                    key={i}
                    item={item}
                    repIndex={repIndex[item.ingredient_id] ?? 0}
                    isChecked={checked.has(item.ingredient_id)}
                    onToggle={() => toggleChecked(item.ingredient_id)}
                    onCycleRep={() => cycleRep(item.ingredient_id, item.representations.length)}
                  />
                ))}
              </TypeSection>
            ))}
          </Card>
        )}
      </section>
    </div>
  );
}
